import { Phone } from 'lucide-react'
import { useCallProvider } from './CallProvider'
import { CATEGORICAL } from '@/lib/theme'

interface ClickToCallButtonProps {
  phoneNumber: string
  formattedPhone?: string
  /** Contact or Lead ID */
  whoId?: string
  /** 'Contact' or 'Lead' */
  whoType?: string
  /** Display name of the person */
  whoName?: string
  /** Account, Opportunity, etc. ID */
  whatId?: string
  /** Entity type for what_id */
  whatType?: string
  /** Display name for what entity */
  whatName?: string
  /** If true, the person has opted out of calls */
  doNotCall?: boolean
  /** Visual style variant */
  variant?: 'link' | 'icon' | 'button'
  /** Additional CSS class */
  className?: string
}

export function ClickToCallButton({
  phoneNumber,
  formattedPhone,
  whoId,
  whoType,
  whoName,
  whatId,
  whatType,
  whatName,
  doNotCall = false,
  variant = 'link',
  className = '',
}: ClickToCallButtonProps) {
  const { openCallDialog, startCall } = useCallProvider()

  const displayPhone = formattedPhone || phoneNumber

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (doNotCall) {
      // TODO Phase 2: Show do-not-call warning dialog
      return
    }

    const context = {
      entity: {
        who_id: whoId,
        who_type: whoType,
        who_name: whoName,
        what_id: whatId,
        what_type: whatType,
        what_name: whatName,
        phone_number: phoneNumber,
        formatted_phone: formattedPhone,
      },
    }

    // Start the timer and open the dialog
    startCall(context)
    openCallDialog(context)

    // Trigger the tel: URI to launch Zoiper
    window.location.href = `tel:${phoneNumber}`
  }

  const ariaLabel = whoName ? `Call ${whoName} at ${displayPhone}` : `Call ${displayPhone}`

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        className={`inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted transition-colors ${doNotCall ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        aria-label={ariaLabel}
        disabled={doNotCall}
        title={doNotCall ? 'Do Not Call' : ariaLabel}
      >
        <Phone className="h-4 w-4" aria-hidden="true" />
      </button>
    )
  }

  if (variant === 'button') {
    return (
      <button
        onClick={handleClick}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors ${doNotCall ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        aria-label={ariaLabel}
        disabled={doNotCall}
        title={doNotCall ? 'Do Not Call' : undefined}
      >
        <Phone className="h-4 w-4" aria-hidden="true" />
        {displayPhone}
      </button>
    )
  }

  // Default: link style (looks like existing tel: links)
  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1 text-sm hover:underline hover:opacity-80 ${doNotCall ? 'opacity-50 cursor-not-allowed line-through' : ''} ${className}`}
      style={{ color: CATEGORICAL.navy.solid }}
      aria-label={ariaLabel}
      disabled={doNotCall}
      title={doNotCall ? 'Do Not Call' : undefined}
    >
      {displayPhone}
    </button>
  )
}
